/**
 * Copyright 2022 Phenix Real Time Solutions, Inc. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {h, Component} from 'preact';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import Seekbar from '../components/controls/Seekbar.jsx';
import {seekToRealtimeStreamAction} from '../actions/video.js';
import {
  getCurrentTime,
  getBufferDuration,
  getBufferStart
} from '../reducers/video';
import {getLag} from '../reducers/statistics';
import {seekToAction} from '../actions/video';
import {getRenderer} from '../reducers/stream';

class SeekbarWrapper extends Component {
  constructor(props) {
    super(props);

    this.timeSlider = null;
    this.pointOfTime = '0:00';
    this.timeSliderOffset = 5;

    this.seekTo = this.seekTo.bind(this);
    this.mouseEnter = this.mouseEnter.bind(this);
    this.mouseMove = this.mouseMove.bind(this);
    this.mouseLeave = this.mouseLeave.bind(this);
    this.setTimeSlider = this.setTimeSlider.bind(this);
    this.changeTimeSliderPositionAndTime =
      this.changeTimeSliderPositionAndTime.bind(this);
  }

  seekTo(sliderPosition) {
    this.props.seekTo(parseInt(sliderPosition) + this.props.bufferStart);
  }

  changeTimeSliderPositionAndTime(clientX, offsetX) {
    this.timeSlider.style.left = `${
      clientX -
      parseInt(this.timeSlider.getBoundingClientRect().width) / 2 -
      this.timeSliderOffset
    }px`;

    const slider = document.getElementById('slider');
    const sliderWidth = slider.getBoundingClientRect().width;
    const lengthOfDivision =
      sliderWidth / (this.props.bufferDuration + this.props.lag);
    const numberOfDivision = parseInt(offsetX / lengthOfDivision);
    const video = document.getElementById(this.props.videoId);

    if (video) {
      if (video.buffered && video.buffered.length) {
        const bufferedStart = parseInt(video.buffered.start(0));
        const countOfSeconds = numberOfDivision + bufferedStart;

        if (countOfSeconds > 59 && countOfSeconds < 3600) {
          const countOfMinutes = parseInt(countOfSeconds / 60);
          const restOfSeconds = countOfSeconds - countOfMinutes * 60;

          this.pointOfTime = `${countOfMinutes}:${
            restOfSeconds < 10 ? `0${restOfSeconds}` : restOfSeconds
          }`;
        } else if (countOfSeconds >= 3600) {
          const countOfHours = parseInt(countOfSeconds / 3600);
          let restOfSeconds = countOfSeconds - countOfHours * 3600;
          const countOfMinutes = parseInt(restOfSeconds / 60);
          restOfSeconds = restOfSeconds - countOfMinutes * 60;
          this.pointOfTime = `${countOfHours}:${
            countOfMinutes < 10 ? `0${countOfMinutes}` : countOfMinutes
          }:${restOfSeconds < 10 ? `0${restOfSeconds}` : restOfSeconds}`;
        } else {
          this.pointOfTime =
            countOfSeconds < 10
              ? `0:0${countOfSeconds}`
              : `0:${countOfSeconds}`;
        }
      }
    }
  }

  mouseEnter(clientX, offsetX) {
    if (this.timeSlider) {
      this.changeTimeSliderPositionAndTime(clientX, offsetX);
      this.timeSlider.style.visibility = 'visible';
    }
  }

  mouseMove(clientX, offsetX) {
    if (this.timeSlider) {
      this.changeTimeSliderPositionAndTime(clientX, offsetX);
    }
  }

  mouseLeave() {
    if (this.timeSlider) {
      this.timeSlider.style.visibility = 'hidden';
    }
  }

  setTimeSlider(element) {
    this.timeSlider = element;
  }

  render() {
    return (
      <Seekbar
        currentTime={this.props.currentTime}
        bufferDuration={this.props.bufferDuration}
        pointOfTime={this.pointOfTime}
        seekTo={this.seekTo}
        mouseEnter={this.mouseEnter}
        mouseMove={this.mouseMove}
        mouseLeave={this.mouseLeave}
        seekToRealtimeStream={this.props.seekToRealtimeStream}
        lag={this.props.lag}
        timeSliderRef={this.setTimeSlider}
      />
    );
  }
}

SeekbarWrapper.propTypes = {
  videoId: PropTypes.string.isRequired,
  bufferDuration: PropTypes.number.isRequired,
  bufferStart: PropTypes.number.isRequired,
  currentTime: PropTypes.number.isRequired,
  lag: PropTypes.number.isRequired,
  video: PropTypes.object,

  seekTo: PropTypes.func.isRequired,
  seekToRealtimeStream: PropTypes.func.isRequired
};

const mapStateToProps = (state, {video}) => {
  return {
    bufferDuration: getBufferDuration(state) + getLag(state),
    bufferStart: getBufferStart(state),
    currentTime: getCurrentTime(state),
    lag: getLag(state),
    renderer: getRenderer(state),
    video
  };
};

const mapDispatchToProps = (dispatch, {video}) => {
  return {
    seekTo: (timestamp) => dispatch(seekToAction(video, timestamp)),
    seekToRealtimeStream: () => dispatch(seekToRealtimeStreamAction(video))
  };
};

const SeekbarContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(SeekbarWrapper);

export default SeekbarContainer;